import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { Formik, Form } from "formik";
import {
  Grid,
  Modal,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { useSnackbar } from "notistack";
import { useMaterialUIController } from "context";

import Promover from "./components/promover_info";
import validations from "./schemas/validations";
import form from "./schemas/form";
import initialValues from "./schemas/initialValues";

import { useGetCiclosQuery, usePromoverGrupoEscolarMutation } from "services";

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === "dark"
      ? "rgba(255, 255, 255, 0.08)"
      : "rgba(0, 0, 0, 0.04)",
  borderRadius: "12px",
  padding: "4px",
  "& .MuiToggleButtonGroup-grouped": {
    margin: "0px",
    border: 0,
    borderRadius: "8px",
    textTransform: "none",
    fontWeight: 600,
    padding: "8px 24px",
    color:
      theme.palette.mode === "dark"
        ? theme.palette.text.secondary
        : theme.palette.text.primary,
    "&.Mui-selected": {
      backgroundColor: theme.palette.info.main,
      color: "#ffffff",
      boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.12)",
      "&:hover": {
        backgroundColor: theme.palette.info.dark,
      },
    },
    "&:not(:first-of-type)": {
      borderRadius: "8px",
    },
    "&:first-of-type": {
      borderRadius: "8px",
    },
  },
}));

const PromoverGrupoEscolar = ({ open, handleClose }) => {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const { enqueueSnackbar } = useSnackbar();

  const currentValidation = validations[0];

  const { data: ciclos } = useGetCiclosQuery(undefined, {
    refetchOnReconnect: true,
  });

  const [promoverGrupo] = usePromoverGrupoEscolarMutation();

  const { formId, formField } = form;

  const submitForm = async (values, actions) => {
    try {
      await promoverGrupo({
        id: values.grupo_id_clonar,
        identificador: values.identificador,
      }).unwrap();

      enqueueSnackbar("Grupo Escolar promovido con éxito", {
        variant: "success",
      });
      actions.setSubmitting(false);
      actions.resetForm();
      handleClose();
    } catch (error) {
      console.error(error);
      enqueueSnackbar(
        error?.data?.detail || "Error al promover el Grupo Escolar",
        { variant: "error" },
      );
      actions.setSubmitting(false);
    }
  };

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "90%",
    maxWidth: 900,
    boxShadow: 24,
    borderRadius: "12px",
    px: "40px",
    py: "29px",
    outline: "none",
    maxHeight: "95vh",
    overflowY: "auto",
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <MDBox sx={modalStyle} bgColor={darkMode ? "black" : "white"}>
        <MDBox mb={3} textAlign="center">
          <MDTypography variant="h4" fontWeight="bold">
            Promover Grupo Escolar
          </MDTypography>
          <MDTypography variant="body2" color="secondary">
            Verifique la información mostrada para promover un grupo a otro
            semestre
          </MDTypography>
        </MDBox>
        <Formik
          initialValues={initialValues}
          onSubmit={submitForm}
          validationSchema={currentValidation}
        >
          {({ values, errors, touched, setFieldValue, isSubmitting }) => {
            useEffect(() => {
              if (values.clonar === "Actual" && ciclos) {
                setFieldValue(
                  formField.ciclo_id.name,
                  ciclos[ciclos?.length - 1]?.id,
                );
              } else if (values.clonar === "Anterior" && ciclos) {
                setFieldValue(
                  formField.ciclo_id.name,
                  ciclos[ciclos?.length - 2]?.id,
                );
              }
            }, [ciclos, values.clonar, setFieldValue, formField.ciclo_id.name]);

            return (
              <Form id={formId} autoComplete="off">
                <MDBox display="flex" justifyContent="center" mb={4} mt={1}>
                  <Tooltip
                    placement="top"
                    title="Seleccione el ciclo (actual o anterior) para ver sus grupos"
                  >
                    <StyledToggleButtonGroup
                      value={values.clonar}
                      exclusive
                      onChange={(e, newValue) => {
                        if (newValue !== null && values.clonar !== newValue) {
                          setFieldValue(formField.clonar.name, newValue);
                        }
                      }}
                      aria-label="ciclo a clonar"
                    >
                      <ToggleButton
                        value="Anterior"
                        aria-label="anterior"
                        disabled={ciclos?.length <= 1}
                      >
                        Ciclo Anterior
                      </ToggleButton>
                      <ToggleButton value="Actual" aria-label="actual">
                        Ciclo Actual
                      </ToggleButton>
                    </StyledToggleButtonGroup>
                  </Tooltip>
                </MDBox>

                <MDBox>
                  {values.ciclo_id !== null && ciclos?.length > 0 && (
                    <Promover
                      formData={{
                        values,
                        touched,
                        formField,
                        errors,
                        setFieldValue,
                      }}
                    />
                  )}
                  <Grid
                    item
                    xs={12}
                    display="flex"
                    justifyContent="center"
                    gap={2}
                    mt={4}
                  >
                    <MDButton
                      variant="contained"
                      color="accent"
                      type="submit"
                      disabled={isSubmitting}
                    >
                      Aceptar
                    </MDButton>
                    <MDButton
                      variant="outlined"
                      color="accent"
                      onClick={handleClose}
                    >
                      Cancelar
                    </MDButton>
                  </Grid>
                </MDBox>
              </Form>
            );
          }}
        </Formik>
      </MDBox>
    </Modal>
  );
};

PromoverGrupoEscolar.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
};

export default PromoverGrupoEscolar;
